// Simulador de Workflow Empty Container - CORREGIDO
import React from "react";
import { Button, message, Popconfirm } from "antd";
import { PlayCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { useWorkflowStoreEmptyZero } from "../../store";
import { useWidth } from "../../../../libs/hooks";

interface WorkflowSimulatorProps {
  onComplete?: () => void;
  disabled?: boolean;
}

export const WorkflowSimulator: React.FC<WorkflowSimulatorProps> = ({
  onComplete,
  disabled = false,
}) => {
  const width = useWidth();
  const isMobile = width === "xs" || width === "sm";
  const [isSimulating, setIsSimulating] = React.useState(false);

  // Obtener todas las funciones del store
  const {
    setContainer,
    setClientId,
    setProducerId,
    setFarmId,
    setBrandId,
    setBoxId,
    setMagap,
    setNumKit,
    setObservation,
    setStartProcess,
    setContainerArrival,
    setContainerOpen,
    setStartLoading,
    setEir,
    setCiConductor,
    setPlaca,
    setEtiquetaVerificadoraPuerta,
    setEtiquetasVentoleras,
    setEtiquetasPanelExterior,
    setEtiquetasPanelInterior,
    setPlastico,
    setBotellaRastreo,
    setNumRastreoSatelital,
    setEtiquetaExporPuerta,
    setMacroEtiquetasVentoleras,
    setMacroEtiquetasVentolerasAdicional,
    setCalidad,
    setGancho,
    setDriver,
    setCompanyTransport,
    // Funciones de fotos
    setSiglasEPhoto,
    setEirPhoto,
    setCiConductorPhoto,
    setCabezalPhoto,
    setPlacaPhoto,
    setEtiquetaVerificadoraPuertaPhoto,
    setEtiquetasVentolerasPhoto1,
    setEtiquetasVentolerasPhoto2,
    setEtiquetasPanelExteriorPhoto1,
    setEtiquetasPanelExteriorPhoto2,
    setEtiquetasPanelInteriorPhoto1,
    setEtiquetasPanelInteriorPhoto2,
    setEtiquetasPanelInteriorPhoto3,
    setPlasticoPhoto,
    setBotellaRastreoPhoto,
    setNumRastreoSatelitalPhoto,
    setEtiquetaExporPuertaPhoto,
    setMacroEtiquetasVentolerasPhoto1,
    setMacroEtiquetasVentolerasPhoto2,
    setMacroEtiquetasVentolerasAdicionalPhoto1,
    setMacroEtiquetasVentolerasAdicionalPhoto2,
    setParedExternaLadoDerechoPhoto,
    setParedExternaLadoIzquierdoPhoto,
    setPanelExteriorPhoto,
    setTechoExteriorPhoto,
    setPisoDerechoExteriorPhoto,
    setPisoIzquierdoExteriorPhoto,
    setParedInternaLadoDerechoPhoto,
    setParedInternaLadoIzquierdoPhoto,
    setPanelInternoPhoto1,
    setPanelInternoPhoto2,
    setPanelInternoPhoto3,
    setPanelInternoPhoto4,
    setTechoInternoPhoto,
    setPisoInternoPhoto,
    setParchesPhoto1,
    setParchesPhoto2,
    setParchesPhoto3,
    setParchesExtPhoto1,
    setParchesExtPhoto2,
    setParchesExtPhoto3,
  } = useWorkflowStoreEmptyZero();

  // Función para generar imagen placeholder y convertirla a blob URL
  const generatePlaceholderImage = (
    text: string,
    width = 400,
    height = 300
  ): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        // Fondo
        ctx.fillStyle = "#f0f0f0";
        ctx.fillRect(0, 0, width, height);

        // Texto
        ctx.fillStyle = "#333";
        ctx.font = "16px Arial";
        ctx.textAlign = "center";
        ctx.fillText(text, width / 2, height / 2);

        // Timestamp
        ctx.font = "12px Arial";
        ctx.fillText(new Date().toLocaleString(), width / 2, height / 2 + 25);
      }

      // Convertir canvas a blob y luego crear URL
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const blobUrl = URL.createObjectURL(blob);
            resolve(blobUrl);
          } else {
            resolve(canvas.toDataURL("image/jpeg", 0.8));
          }
        },
        "image/jpeg",
        0.8
      );
    });
  };

  const simulateWorkflow = async () => {
    try {
      setIsSimulating(true);
      message.loading("Simulando workflow completo...", 0);

      // Simular demora realista
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // 1. DATOS BÁSICOS DEL FORMULARIO
      const containerNumber = `SIMU${Date.now().toString().slice(-6)}`;
      setContainer(containerNumber);
      setClientId(1);
      setProducerId(1);
      setFarmId(1);
      setBrandId(1);
      setBoxId(1);
      setMagap(`MGP${Date.now().toString().slice(-4)}`);
      setNumKit(`KIT${Date.now().toString().slice(-3)}`);
      setObservation("Datos simulados para testing");

      // 2. HORARIOS
      const now = new Date();
      const startTime = new Date(now.getTime() - 4 * 60 * 60 * 1000);
      const arrivalTime = new Date(now.getTime() - 3 * 60 * 60 * 1000);
      const openTime = new Date(now.getTime() - 2 * 60 * 60 * 1000);
      const loadingTime = new Date(now.getTime() - 1 * 60 * 60 * 1000);

      setStartProcess(startTime.toTimeString().slice(0, 5));
      setContainerArrival(arrivalTime.toTimeString().slice(0, 5));
      setContainerOpen(openTime.toTimeString().slice(0, 5));
      setStartLoading(loadingTime.toTimeString().slice(0, 5));

      await new Promise((resolve) => setTimeout(resolve, 300));

      // 3. CÓDIGOS Y DATOS
      setEir(`EIR${Date.now().toString().slice(-6)}`);
      setCiConductor(`1234567890`);
      setPlaca(`ABC-${Date.now().toString().slice(-3)}`);
      setEtiquetaVerificadoraPuerta(`EVP${Date.now().toString().slice(-4)}`);
      setEtiquetasVentoleras(`EV${Date.now().toString().slice(-5)}`);
      setEtiquetasPanelExterior(`EPE${Date.now().toString().slice(-4)}`);
      setEtiquetasPanelInterior(`EPI${Date.now().toString().slice(-4)}`);
      setPlastico(`PLS${Date.now().toString().slice(-3)}`);
      setBotellaRastreo(`BR${Date.now().toString().slice(-4)}`);
      setNumRastreoSatelital(`NRS${Date.now().toString().slice(-5)}`);
      setEtiquetaExporPuerta(`EEP${Date.now().toString().slice(-4)}`);
      setMacroEtiquetasVentoleras(`MEV${Date.now().toString().slice(-4)}`);
      setMacroEtiquetasVentolerasAdicional(
        `MEVA${Date.now().toString().slice(-3)}`
      );

      setDriver("Juan Pérez Conductor");
      setCompanyTransport("Transportes Simulados S.A.");
      setCalidad("Carlos Calidad");
      setGancho("Pedro Gancho");

      await new Promise((resolve) => setTimeout(resolve, 300));
      message.destroy();
      message.loading("Generando fotos simuladas...", 0);

      // 4. GENERAR TODAS LAS FOTOS REQUERIDAS
      const photosToGenerate = [
        { setter: setSiglasEPhoto, label: `Contenedor ${containerNumber}` },
        { setter: setEirPhoto, label: "EIR Document" },
        { setter: setCiConductorPhoto, label: "CI Conductor" },
        { setter: setCabezalPhoto, label: "Cabezal Camión" },
        { setter: setPlacaPhoto, label: "Placa Vehículo" },
        {
          setter: setEtiquetaVerificadoraPuertaPhoto,
          label: "Etiq. Verificadora",
        },
        { setter: setEtiquetasVentolerasPhoto1, label: "Etiq. Ventoleras 1" },
        { setter: setEtiquetasVentolerasPhoto2, label: "Etiq. Ventoleras 2" },
        { setter: setEtiquetasPanelExteriorPhoto1, label: "Panel Ext. 1" },
        { setter: setEtiquetasPanelExteriorPhoto2, label: "Panel Ext. 2" },
        { setter: setEtiquetasPanelInteriorPhoto1, label: "Panel Int. 1" },
        { setter: setEtiquetasPanelInteriorPhoto2, label: "Panel Int. 2" },
        { setter: setEtiquetasPanelInteriorPhoto3, label: "Panel Int. 3" },
        { setter: setPlasticoPhoto, label: "Plástico" },
        { setter: setBotellaRastreoPhoto, label: "Botella Rastreo" },
        { setter: setNumRastreoSatelitalPhoto, label: "Num. Satelital" },
        { setter: setEtiquetaExporPuertaPhoto, label: "Etiq. Exportador" },
        { setter: setMacroEtiquetasVentolerasPhoto1, label: "Macro Etiq. 1" },
        { setter: setMacroEtiquetasVentolerasPhoto2, label: "Macro Etiq. 2" },
        {
          setter: setMacroEtiquetasVentolerasAdicionalPhoto1,
          label: "Pack Sellos Izq.",
        },
        {
          setter: setMacroEtiquetasVentolerasAdicionalPhoto2,
          label: "Pack Sellos Der.",
        },
        {
          setter: setParedExternaLadoDerechoPhoto,
          label: "Pared Ext. Derecha",
        },
        {
          setter: setParedExternaLadoIzquierdoPhoto,
          label: "Pared Ext. Izquierda",
        },
        { setter: setPanelExteriorPhoto, label: "Panel Exterior" },
        { setter: setTechoExteriorPhoto, label: "Techo Exterior" },
        { setter: setPisoDerechoExteriorPhoto, label: "Piso Der. Ext." },
        { setter: setPisoIzquierdoExteriorPhoto, label: "Piso Izq. Ext." },
        {
          setter: setParedInternaLadoDerechoPhoto,
          label: "Pared Int. Derecha",
        },
        {
          setter: setParedInternaLadoIzquierdoPhoto,
          label: "Pared Int. Izquierda",
        },
        { setter: setPanelInternoPhoto1, label: "Panel Interno 1" },
        { setter: setPanelInternoPhoto2, label: "Panel Interno 2" },
        { setter: setPanelInternoPhoto3, label: "Panel Interno 3" },
        { setter: setPanelInternoPhoto4, label: "Panel Interno 4" },
        { setter: setTechoInternoPhoto, label: "Techo Interno" },
        { setter: setPisoInternoPhoto, label: "Piso Interno" },
        { setter: setParchesPhoto1, label: "Parches Interior 1" },
        { setter: setParchesPhoto2, label: "Parches Interior 2" },
        { setter: setParchesPhoto3, label: "Parches Interior 3" },
        { setter: setParchesExtPhoto1, label: "Parches Ext. 1" },
        { setter: setParchesExtPhoto2, label: "Parches Ext. 2" },
        { setter: setParchesExtPhoto3, label: "Parches Ext. 3" },
      ];

      // Generar y asignar cada foto
      for (let i = 0; i < photosToGenerate.length; i++) {
        const { setter, label } = photosToGenerate[i];

        try {
          // Generar la imagen como blob URL
          const imageBlobUrl = await generatePlaceholderImage(label);

          // Llamar al setter con la imagen generada
          await setter(imageBlobUrl);
        } catch (error) {
          console.warn(`Error generando foto ${label}:`, error);
        }

        // Pequeña pausa entre fotos
        if (i % 5 === 0) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }

      message.destroy();
      message.success(
        "Workflow simulado completamente! Todos los campos están llenos."
      );

      onComplete?.();
    } catch (error) {
      console.error("Error simulando workflow:", error);
      message.destroy();
      message.error("Error durante la simulación");
    } finally {
      setIsSimulating(false);
    }
  };

  return (
    <Popconfirm
      title="Simular Workflow Completo"
      description="¿Llenar automáticamente todos los campos con datos de prueba?"
      onConfirm={simulateWorkflow}
      okText="Sí, simular"
      cancelText="Cancelar"
      zIndex={1601}
      icon={<PlayCircleOutlined style={{ color: "#52c41a" }} />}
    >
      {isMobile ? (
        <Button
          size="middle"
          type="default"
          icon={isSimulating ? <LoadingOutlined /> : <PlayCircleOutlined />}
          disabled={disabled || isSimulating}
          loading={isSimulating}
          style={{
            backgroundColor: "#f6ffed",
            borderColor: "#52c41a",
            color: "#52c41a",
          }}
        />
      ) : (
        <Button
          size="middle"
          type="default"
          icon={isSimulating ? <LoadingOutlined /> : <PlayCircleOutlined />}
          disabled={disabled || isSimulating}
          loading={isSimulating}
          style={{
            backgroundColor: "#f6ffed",
            borderColor: "#52c41a",
            color: "#52c41a",
          }}
        >
          {isSimulating ? "Simulando..." : "Simular"}
        </Button>
      )}
    </Popconfirm>
  );
};

export default WorkflowSimulator;
